/**
 * Socket.IO client configuration for real-time WebSocket events.
 *
 * This module provides the Socket.IO client instance and connection
 * management functions for real-time communication with the web-backend.
 *
 * Features:
 * - Configure connection with auth token from Keycloak
 * - Handle connection lifecycle (connect, disconnect, error, reconnect)
 * - One shared connection, reference counted (acquireConnection)
 * - Event subscriptions that may be registered before the socket exists
 * - Export socket instance and connection functions
 */

import { io, Socket } from 'socket.io-client';
import { getToken, refreshToken } from '@/lib/keycloak';
import { withTimeout } from '@/lib/withTimeout';
import type { ConnectionState, WebSocketEventType, WebSocketEventDataMap } from '@/types/websocket';

/**
 * Where the web-backend serves Socket.IO.
 *
 * web-backend mounts the Socket.IO ASGI app at `/ws`, which puts the ENGINE
 * endpoint at `/ws/socket.io/`. That is the Engine.IO `path` option. It is
 * NOT a namespace: in socket.io-client a path appended to the URL passed to
 * `io()` selects a namespace, so `io(origin + '/ws/socket.io')` requests the
 * default engine endpoint `/socket.io/` (the web-frontend, via the default
 * HTTPRoute rule) and asks for a namespace the server never defined. The
 * client must therefore connect to the ORIGIN, with `path` set, on the
 * default namespace `/`.
 *
 * `VITE_WS_URL` may override the base (default `/ws`), either a path on this
 * origin or an absolute http(s)/ws(s) URL whose path is the mount prefix.
 */
const DEFAULT_SOCKET_BASE = '/ws';
const ENGINE_PATH_SUFFIX = '/socket.io';

export interface SocketTarget {
  /** Origin only (no path): the namespace stays the default `/`. */
  url: string;
  /** Engine.IO endpoint path, no trailing slash (the client adds one). */
  path: string;
}

/**
 * Split a configured base (`/ws`, `https://host/ws`, `wss://host/ws`) into the
 * origin to connect to and the Engine.IO path.
 */
export function resolveSocketTarget(
  configuredBase: string | undefined,
  pageOrigin: string
): SocketTarget {
  const base =
    typeof configuredBase === 'string' && configuredBase.trim() !== ''
      ? configuredBase.trim()
      : DEFAULT_SOCKET_BASE;
  let parsed: URL;
  try {
    parsed = new URL(base, pageOrigin);
  } catch {
    parsed = new URL(DEFAULT_SOCKET_BASE, pageOrigin);
  }
  const prefix = parsed.pathname.replace(/\/+$/, '');
  // ws(s) schemes are accepted in config but handed to io() as http(s).
  const protocol =
    parsed.protocol === 'wss:' ? 'https:' : parsed.protocol === 'ws:' ? 'http:' : parsed.protocol;
  return { url: `${protocol}//${parsed.host}`, path: `${prefix}${ENGINE_PATH_SUFFIX}` };
}

/**
 * Reconnection configuration.
 */
const RECONNECTION_CONFIG = {
  /** Enable automatic reconnection */
  reconnection: true,
  /** No cap: a long outage must heal itself when the network returns */
  reconnectionAttempts: Infinity,
  /** Initial delay between reconnection attempts (ms) */
  reconnectionDelay: 1000,
  /** Maximum delay between reconnection attempts (ms) */
  reconnectionDelayMax: 10000,
  /** Randomization factor for reconnection delays */
  randomizationFactor: 0.5,
  /** Connection timeout (ms) */
  timeout: 20000,
};

/** Longest a token refresh may hold up a connect or reconnect handshake. */
const AUTH_REFRESH_TIMEOUT_MS = 8_000;

/** Manual reconnect backoff (after connect_error or a server side disconnect). */
const MANUAL_RECONNECT_BASE_MS = 1_000;
const MANUAL_RECONNECT_MAX_MS = 30_000;

/** Longest the page waits before nudging a dead socket again (visibility/online). */
const NUDGE_MIN_INTERVAL_MS = 5_000;

/**
 * How long after the last holder lets go the socket is actually closed. A
 * React StrictMode remount, a route change that remounts the owner, or a quick
 * re-auth gap re-acquires inside this window and reuses the open connection
 * instead of tearing it down and handshaking again.
 */
export const RELEASE_GRACE_MS = 1_000;

/** Whether the socket has connected at least once (so a connect is a RE-connect). */
let hasConnectedOnce = false;
/** Consecutive failed attempts since the last good connection (log throttle). */
let failureStreak = 0;
let manualReconnectTimer: ReturnType<typeof setTimeout> | null = null;
let manualReconnectAttempt = 0;
/** True after a deliberate disconnect(): never reconnect on our own then. */
let intentionalDisconnect = false;

/**
 * Socket.IO client instance.
 * Initially null until connect() is called.
 */
let socketInstance: Socket | null = null;

/**
 * Current connection state.
 */
let connectionState: ConnectionState = 'disconnected';

/**
 * Application event subscriptions (chat_title_updated, chat_progress, ...).
 *
 * Kept here, not only on the socket, so a subscription made BEFORE the socket
 * exists (hooks mount before the owner connects, and the socket is created
 * lazily) is attached when the socket is created, and so a subscription
 * outlives a socket that is torn down and rebuilt. socket.io keeps listeners
 * across its own reconnects, so a plain reconnect needs no re-attach.
 */
interface AppSubscription {
  event: string;
  handler: (...args: unknown[]) => void;
}
const appSubscriptions = new Set<AppSubscription>();

/** Number of live owners that asked for a connection (see acquireConnection). */
let connectionHolders = 0;
let releaseTimer: ReturnType<typeof setTimeout> | null = null;
let lifecycleInstalled = false;
let lastNudgeAt = 0;

/**
 * Registered event listeners for connection events.
 */
type ConnectionEventListener = (data?: unknown) => void;
const connectionListeners: Map<string, Set<ConnectionEventListener>> = new Map();

/**
 * Get the current connection state.
 */
export function getConnectionState(): ConnectionState {
  return connectionState;
}

/**
 * Set connection state and notify listeners.
 */
function setConnectionState(state: ConnectionState, data?: unknown): void {
  connectionState = state;
  const listeners = connectionListeners.get('state');
  if (listeners) {
    listeners.forEach((listener) => {
      listener({ state, data });
    });
  }
}

/**
 * Create and configure a new Socket.IO client instance.
 */
function createSocket(): Socket {
  const { url, path } = resolveSocketTarget(
    import.meta.env.VITE_WS_URL as string | undefined,
    window.location.origin
  );

  // Origin as the URL (default namespace) and the mount as the engine `path`.
  const socket = io(url, {
    path,
    ...RECONNECTION_CONFIG,
    // Function form: evaluated on EVERY handshake, reconnects included, so a
    // reconnect after the access token expired never replays the old one.
    auth: (cb) => {
      void (async () => {
        await withTimeout(refreshToken(30), AUTH_REFRESH_TIMEOUT_MS, false);
        cb({ token: getToken() ?? '' });
      })();
    },
    // Websocket only: the LB has no session affinity for long polling, which
    // is what produced "session id unknown" churn across replicas.
    transports: ['websocket'],
    forceNew: false,
    autoConnect: false,
  });

  // Set up connection lifecycle handlers
  setupConnectionHandlers(socket);

  // Subscriptions registered before this socket existed.
  appSubscriptions.forEach((sub) => {
    socket.on(sub.event, sub.handler);
  });

  return socket;
}

/**
 * Set up handlers for Socket.IO connection lifecycle events.
 */
function setupConnectionHandlers(socket: Socket): void {
  socket.on('connect', () => {
    // Counts only: never the socket id, the URL or a payload.
    if (failureStreak > 0) {
      console.info('Socket.IO connected after failed attempts:', failureStreak);
    }
    failureStreak = 0;
    clearManualReconnect();
    manualReconnectAttempt = 0;
    setConnectionState('connected');
    notifyListeners('connect', { socketId: socket.id });
    // Updates emitted while the socket was down are gone: tell the app to
    // resync. Not on the very first connect.
    if (hasConnectedOnce) notifyListeners('resync', {});
    hasConnectedOnce = true;
  });

  socket.on('disconnect', (reason) => {
    setConnectionState('disconnected', reason);
    notifyListeners('disconnect', { reason });
    // The client only retries by itself for transport level drops. A server
    // initiated disconnect leaves the socket inactive, so retry ourselves.
    if (reason === 'io server disconnect') scheduleManualReconnect(socket);
  });

  socket.on('connect_error', (error) => {
    // First failure of a streak, then every tenth: a down backend must not
    // fill the console at one line per retry.
    failureStreak += 1;
    if (failureStreak === 1 || failureStreak % 10 === 0) {
      console.warn('Socket.IO connection failed, attempts in a row:', failureStreak);
    }
    setConnectionState('error', error);
    notifyListeners('error', { error });
    // A rejected handshake (auth middleware) is not retried by socket.io.
    scheduleManualReconnect(socket);
  });

  socket.io.on('reconnect_attempt', (attempt) => {
    setConnectionState('reconnecting', attempt);
    notifyListeners('reconnect_attempt', { attempt });
  });

  socket.io.on('reconnect', (attempt) => {
    setConnectionState('connected');
    notifyListeners('reconnect', { attempt });
  });

  socket.io.on('reconnect_failed', () => {
    setConnectionState('error', 'reconnection_failed');
    notifyListeners('reconnect_failed', {});
  });

  socket.io.on('reconnect_error', (error) => {
    notifyListeners('reconnect_error', { error });
  });
}

function clearManualReconnect(): void {
  if (manualReconnectTimer !== null) {
    clearTimeout(manualReconnectTimer);
    manualReconnectTimer = null;
  }
}

/**
 * Reconnect with exponential backoff and jitter, without an attempt cap.
 * A no-op while socket.io is retrying by itself (`socket.active`), after a
 * deliberate disconnect(), or when a retry is already queued.
 */
function scheduleManualReconnect(socket: Socket): void {
  if (intentionalDisconnect || socket.active || socket.connected) return;
  if (manualReconnectTimer !== null) return;
  const ceiling = Math.min(
    MANUAL_RECONNECT_MAX_MS,
    MANUAL_RECONNECT_BASE_MS * 2 ** manualReconnectAttempt
  );
  // Full jitter over the upper half, so replicas coming back do not stampede.
  const delay = Math.round(ceiling * (0.5 + Math.random() * 0.5));
  manualReconnectAttempt += 1;
  manualReconnectTimer = setTimeout(() => {
    manualReconnectTimer = null;
    if (intentionalDisconnect || socket.active || socket.connected) return;
    // Offline: the `online` event nudges the reconnect, so do not burn
    // attempts (and backoff) against a network that is known to be down.
    if (typeof navigator !== 'undefined' && !navigator.onLine) return;
    socket.connect();
  }, delay);
}

/**
 * Notify registered listeners of an event.
 */
function notifyListeners(event: string, data: unknown): void {
  const listeners = connectionListeners.get(event);
  if (listeners) {
    listeners.forEach((listener) => {
      listener(data);
    });
  }
}

/**
 * Get the socket instance, creating it if necessary.
 */
export function getSocket(): Promise<Socket> {
  socketInstance ??= createSocket();
  return Promise.resolve(socketInstance);
}

/**
 * Connect to the WebSocket server.
 *
 * @returns Promise that resolves when connected or rejects on error
 *
 * @example
 * ```typescript
 * try {
 *   await connect();
 *   console.log('Connected!');
 * } catch (error) {
 *   console.error('Connection failed:', error);
 * }
 * ```
 */
export async function connect(): Promise<void> {
  const socket = await getSocket();
  intentionalDisconnect = false;

  if (socket.connected) {
    return;
  }

  setConnectionState('connecting');

  return new Promise((resolve, reject) => {
    const connectHandler = () => {
      cleanup();
      resolve();
    };

    const errorHandler = (error: Error) => {
      cleanup();
      reject(error);
    };

    const cleanup = () => {
      socket.off('connect', connectHandler);
      socket.off('connect_error', errorHandler);
    };

    socket.once('connect', connectHandler);
    socket.once('connect_error', errorHandler);

    socket.connect();
  });
}

/**
 * Disconnect from the WebSocket server.
 */
export function disconnect(): void {
  intentionalDisconnect = true;
  clearManualReconnect();
  if (socketInstance) {
    socketInstance.disconnect();
    setConnectionState('disconnected');
  }
}

function cancelPendingRelease(): void {
  if (releaseTimer !== null) {
    clearTimeout(releaseTimer);
    releaseTimer = null;
  }
}

/**
 * Bring a dead socket back now, from a page level cue (the tab became visible,
 * the network came back). A hidden tab's timers are throttled to about one a
 * minute and socket.io's own backoff can be sitting on a long delay, so the
 * cue skips the wait. Rate limited, and never after a deliberate disconnect,
 * while hidden, or while offline.
 */
function nudgeReconnect(): void {
  const socket = socketInstance;
  if (connectionHolders === 0 || !socket || socket.connected) return;
  if (intentionalDisconnect || connectionState === 'connecting') return;
  if (typeof document !== 'undefined' && document.hidden) return;
  if (typeof navigator !== 'undefined' && !navigator.onLine) return;
  const now = Date.now();
  if (now - lastNudgeAt < NUDGE_MIN_INTERVAL_MS) return;
  lastNudgeAt = now;
  clearManualReconnect();
  manualReconnectAttempt = 0;
  // Cancels socket.io's pending backoff timer, if one is armed.
  if (socket.active) socket.disconnect();
  setConnectionState('connecting');
  socket.connect();
}

function installLifecycleListeners(): void {
  if (lifecycleInstalled || typeof window === 'undefined') return;
  lifecycleInstalled = true;
  window.addEventListener('online', nudgeReconnect);
  document.addEventListener('visibilitychange', nudgeReconnect);
}

function removeLifecycleListeners(): void {
  if (!lifecycleInstalled || typeof window === 'undefined') return;
  lifecycleInstalled = false;
  window.removeEventListener('online', nudgeReconnect);
  document.removeEventListener('visibilitychange', nudgeReconnect);
}

/**
 * Ask for the shared connection and get back a function that lets go of it.
 *
 * The first holder opens the socket; further holders reuse it, so duplicate
 * mounts never create a second connection. When the last holder lets go the
 * socket is closed after a short grace period (see RELEASE_GRACE_MS), which
 * is what makes a StrictMode double mount or a remount cost nothing. A
 * connect failure is not thrown here: it surfaces through the connection
 * events and the uncapped, jittered backoff retries it.
 */
export function acquireConnection(): () => void {
  connectionHolders += 1;
  cancelPendingRelease();
  installLifecycleListeners();
  void connect().catch(() => undefined);

  let released = false;
  return () => {
    if (released) return;
    released = true;
    connectionHolders = Math.max(0, connectionHolders - 1);
    if (connectionHolders > 0) return;
    cancelPendingRelease();
    releaseTimer = setTimeout(() => {
      releaseTimer = null;
      if (connectionHolders > 0) return;
      removeLifecycleListeners();
      disconnect();
    }, RELEASE_GRACE_MS);
  };
}

/**
 * Check if the socket is currently connected.
 */
export function isConnected(): boolean {
  return socketInstance?.connected ?? false;
}

/**
 * Re-handshake after the Keycloak token was refreshed.
 *
 * The socket authenticates through a function (see createSocket), which reads
 * the CURRENT token on every handshake, so there is nothing to store here:
 * reconnecting is enough to put the new token in front of the server.
 */
export function updateAuthToken(_token?: string): void {
  if (socketInstance?.connected) {
    socketInstance.disconnect();
    socketInstance.connect();
  }
}

/**
 * Subscribe to a WebSocket event.
 *
 * The server sends events directly (not wrapped in WebSocketEvent envelope),
 * so the callback receives the event data directly.
 *
 * @param eventType - The event type to listen for
 * @param callback - The callback to invoke when the event is received
 * @returns Unsubscribe function
 *
 * @example
 * ```typescript
 * const unsubscribe = subscribeToEvent('message', (data) => {
 *   console.log('Received message:', data.content);
 * });
 *
 * // Later, to unsubscribe:
 * unsubscribe();
 * ```
 */
export function subscribeToEvent<T extends WebSocketEventType>(
  eventType: T,
  callback: (data: WebSocketEventDataMap[T]) => void
): () => void {
  // Socket.IO sends events directly, so we cast the handler
  const handler = (data: unknown) => {
    callback(data as WebSocketEventDataMap[T]);
  };
  return addAppSubscription(eventType, handler);
}

/**
 * Register a listener in the registry and attach it to the live socket if
 * there is one. Without a socket it is attached when the socket is created.
 */
function addAppSubscription(event: string, handler: (...args: unknown[]) => void): () => void {
  const sub: AppSubscription = { event, handler };
  appSubscriptions.add(sub);
  socketInstance?.on(event, handler);
  return () => {
    if (!appSubscriptions.delete(sub)) return;
    socketInstance?.off(event, handler);
  };
}

/**
 * Subscribe to raw Socket.IO events.
 *
 * @param event - The event name
 * @param callback - The callback to invoke
 * @returns Unsubscribe function
 */
export function subscribeToRawEvent(
  event: string,
  callback: (...args: unknown[]) => void
): () => void {
  return addAppSubscription(event, callback);
}

/**
 * Emit an event to the server.
 *
 * @param event - The event name
 * @param data - The data to send
 */
export function emit(event: string, data?: unknown): void {
  socketInstance?.emit(event, data);
}

/**
 * Send a heartbeat to keep the connection alive.
 * The server expects a heartbeat event every 30 seconds.
 *
 * The current access token is included so the server can re-validate it. The
 * socket is authenticated once at connect, so without this a connection would
 * outlive the credential that opened it - the server drops any socket whose
 * heartbeat cannot present a valid token.
 */
export async function sendHeartbeat(): Promise<void> {
  // Refresh before reading the token. getToken() returns whatever keycloak-js
  // holds, which may already be expired between silent refreshes - sending that
  // would fail server-side validation and drop a perfectly good connection. A
  // failed refresh is deliberately not treated as an error: it means the session
  // is genuinely gone, so the heartbeat below is rejected and the server drops
  // the socket, which is the intended outcome.
  try {
    await refreshToken(60);
  } catch {
    // fall through and send whatever we have
  }

  // Checked after the await: the connection may have closed while refreshing.
  if (socketInstance?.connected) {
    socketInstance.emit('heartbeat', { timestamp: Date.now(), token: getToken() });
  }
}

/**
 * Register a listener for connection events.
 *
 * @param event - The event to listen for
 * @param callback - The callback to invoke
 * @returns Unsubscribe function
 *
 * @example
 * ```typescript
 * const unsubscribe = onConnectionEvent('state', ({ state }) => {
 *   console.log('Connection state:', state);
 * });
 * ```
 */
export function onConnectionEvent(
  event:
    | 'connect'
    | 'disconnect'
    | 'error'
    | 'reconnect_attempt'
    | 'reconnect'
    | 'reconnect_failed'
    | 'reconnect_error'
    | 'resync'
    | 'state',
  callback: ConnectionEventListener
): () => void {
  let listeners = connectionListeners.get(event);
  if (!listeners) {
    listeners = new Set();
    connectionListeners.set(event, listeners);
  }
  listeners.add(callback);

  return () => {
    listeners.delete(callback);
  };
}

/**
 * Clean up the socket instance.
 * Call this when the application is unmounting.
 */
export function cleanupSocket(): void {
  if (socketInstance) {
    socketInstance.removeAllListeners();
    socketInstance.disconnect();
    socketInstance = null;
  }
  connectionListeners.clear();
  appSubscriptions.clear();
  connectionState = 'disconnected';
  clearManualReconnect();
  cancelPendingRelease();
  removeLifecycleListeners();
  connectionHolders = 0;
  manualReconnectAttempt = 0;
  failureStreak = 0;
  lastNudgeAt = 0;
  hasConnectedOnce = false;
  intentionalDisconnect = false;
}

/**
 * Export the socket instance getter for direct access if needed.
 * Note: Prefer using the helper functions above instead.
 */
export { socketInstance as socket };
